import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function formatPhoneInput(value) {
  return value.replace(/\D/g, '').slice(0, 10);
}

export default function LoginModal() {
  const navigate = useNavigate();
  const {
    loginModalOpen,
    setLoginModalOpen,
    loginRedirect,
    setLoginRedirect,
    sendOtp,
    verifyOtp,
    clearRecaptcha,
  } = useAuth();

  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!loginModalOpen) {
      setStep('phone');
      setPhone('');
      setOtp('');
      setError('');
      setCountdown(0);
      clearRecaptcha();
    }
  }, [loginModalOpen, clearRecaptcha]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const phoneE164 = phone.length === 10 ? `+91${phone}` : '';

  const handleClose = () => {
    setLoginModalOpen(false);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (phone.length !== 10) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    try {
      await sendOtp(phoneE164);
      setStep('otp');
      setCountdown(30);
    } catch (err) {
      console.error(err);
      setError(getAuthErrorMessage(err));
      clearRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) {
      setError('Enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(otp);
      const redirectTo = loginRedirect || '/profile';
      setLoginRedirect(null);
      setLoginModalOpen(false);
      navigate(redirectTo);
    } catch (err) {
      console.error(err);
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || loading) return;
    setError('');
    setOtp('');
    setLoading(true);
    try {
      await sendOtp(phoneE164);
      setCountdown(30);
    } catch (err) {
      setError(getAuthErrorMessage(err));
      clearRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  if (!loginModalOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[70]"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="relative w-full max-w-md bg-white rounded-sm shadow-2xl border border-gray-100 pointer-events-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-modal-title"
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <div className="px-8 pt-10 pb-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-nude-50 text-charcoal mb-4">
                {step === 'phone' ? <Phone size={20} /> : <ShieldCheck size={20} />}
              </div>
              <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">
                {step === 'phone' ? 'Welcome' : 'Verification'}
              </p>
              <h2 id="login-modal-title" className="font-display text-3xl font-light">
                {step === 'phone' ? 'Sign in with phone' : 'Enter OTP'}
              </h2>
              <p className="text-sm text-gray-500 font-light mt-2">
                {step === 'phone'
                  ? 'We will send a one-time password to verify your number.'
                  : `Code sent to +91 ${phone}`}
              </p>
            </div>

            {step === 'phone' ? (
              <form onSubmit={handleSendOtp} className="space-y-6">
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-2">
                    Mobile number
                  </label>
                  <div className="flex items-center border-b border-gray-200 focus-within:border-black transition-colors">
                    <span className="text-sm text-gray-500 pr-2 py-2.5 font-light">+91</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="9876543210"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                      className="flex-1 outline-none py-2.5 text-sm bg-transparent font-light"
                      autoFocus
                    />
                  </div>
                </div>

                {error && <p className="text-xs text-red-500">{error}</p>}

                <button
                  type="submit"
                  disabled={loading || phone.length !== 10}
                  className="w-full bg-black text-white py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  Send OTP
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-2">
                    6-digit OTP
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="• • • • • •"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full border-b border-gray-200 focus:border-black outline-none py-2.5 text-sm bg-transparent font-light tracking-[0.5em] text-center text-lg"
                    autoFocus
                  />
                </div>

                {error && <p className="text-xs text-red-500 text-center">{error}</p>}

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-black text-white py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  Verify &amp; Continue
                </button>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <button
                    type="button"
                    onClick={() => { setStep('phone'); setOtp(''); setError(''); clearRecaptcha(); }}
                    className="hover:text-black transition-colors"
                  >
                    Change number
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={countdown > 0 || loading}
                    className="hover:text-black transition-colors disabled:text-gray-300"
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div id="recaptcha-container" />
        </div>
      </div>
    </>
  );
}

function getAuthErrorMessage(err) {
  const code = err?.code || '';
  const map = {
    'auth/invalid-phone-number': 'Invalid phone number. Check and try again.',
    'auth/too-many-requests': 'Too many attempts. Please wait and try again.',
    'auth/code-expired': 'OTP expired. Request a new code.',
    'auth/invalid-verification-code': 'Incorrect OTP. Please try again.',
    'auth/missing-verification-code': 'Enter the 6-digit OTP.',
    'auth/captcha-check-failed': 'Verification failed. Refresh and try again.',
    'auth/quota-exceeded': 'SMS quota exceeded. Try again later.',
  };
  return map[code] || err?.message || 'Something went wrong. Please try again.';
}
